import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import styles from "./Button.module.css";

export type ButtonVariant = "filled" | "tinted" | "gray" | "plain" | "destructive";
export type ButtonSize = "sm" | "md" | "lg" | "icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  tint?: string;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "filled",
    size = "md",
    tint,
    fullWidth = false,
    leftIcon,
    rightIcon,
    isLoading = false,
    disabled,
    className,
    children,
    style,
    ...rest
  },
  ref
) {
  const cssVars = tint
    ? ({
        ["--tint" as string]: tint,
        // Derive a subtle tinted gradient so custom-tinted filled buttons keep
        // depth; the default (no tint) keeps the exact brand navy→blue gradient.
        ["--tint-fill" as string]: `linear-gradient(150deg, color-mix(in srgb, ${tint} 82%, #001433) 0%, ${tint} 100%)`,
      } as React.CSSProperties)
    : undefined;
  return (
    <button
      ref={ref}
      className={[
        styles.btn,
        styles[variant],
        styles[size],
        fullWidth ? styles.fullWidth : "",
        className ?? "",
      ].join(" ")}
      style={{ ...cssVars, ...style }}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {isLoading ? (
        <span className={styles.spinner} aria-hidden />
      ) : (
        leftIcon && <span className={styles.iconLeft}>{leftIcon}</span>
      )}
      {children && <span className={styles.label}>{children}</span>}
      {rightIcon && !isLoading && <span className={styles.iconRight}>{rightIcon}</span>}
    </button>
  );
});